import { useTripItineraryImports } from '@hooks'
import { queryKeys } from '@lib/queryKeys'
import {
	GetItineraryImportListItem,
} from '@orbit/api-types'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import {
	black,
	blue,
	darkOrange,
	orange,
	red,
	success,
	textMuted,
	white,
} from '@/theme/colors'

const RECENT_TERMINAL_MS = 30 * 60 * 1000

type BannerVariant = 'progress' | 'success' | 'warn'

interface Props {
	tripId: string
}

const ImportStatusBanner: React.FC<Props> = ({ tripId }) => {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [dismissedId, setDismissedId] = useState<string | null>(null)
	const lastSeenStatusByIdRef = useRef<Map<string, GetItineraryImportListItem['status']>>(new Map())
	const { data } = useTripItineraryImports(tripId)

	useEffect(() => {
		const items = data?.items ?? []
		const lastSeen = lastSeenStatusByIdRef.current
		let shouldInvalidate = false
		for (const item of items) {
			const prev = lastSeen.get(item.id)
			lastSeen.set(item.id, item.status)
			if (
				prev &&
				(prev === 'Pending' || prev === 'Processing') &&
				item.status === 'Success'
			) {
				shouldInvalidate = true
			}
		}
		if (shouldInvalidate) {
			for (const key of queryKeys.itineraryContentKeys(tripId)) {
				queryClient.invalidateQueries({ queryKey: key })
			}
		}
	}, [data, queryClient, tripId])

	const relevant = pickRelevantImport(data?.items ?? [])
	if (!relevant) return null
	if (relevant.id === dismissedId) return null

	const variant = variantForStatus(relevant.status)
	const onOpen = () => {
		navigate(`/trips/${tripId}/import?job=${relevant.id}`)
	}

	return (
		<BannerRoot
			$variant={variant}
			role="button"
			tabIndex={0}
			onClick={onOpen}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					onOpen()
				}
			}}
		>
			<IconBox $variant={variant}>
				{variant === 'progress' ? <Spinner /> : variant === 'success' ? <Glyph>✓</Glyph> : <Glyph>!</Glyph>}
			</IconBox>
			<Message>{messageForItem(relevant)}</Message>
			<CTA>{ctaForVariant(variant)} →</CTA>
			<DismissButton
				type="button"
				aria-label="Dismiss"
				onClick={e => {
					e.stopPropagation()
					setDismissedId(relevant.id)
				}}
			>
				×
			</DismissButton>
		</BannerRoot>
	)
}

export default ImportStatusBanner

const pickRelevantImport = (
	items: GetItineraryImportListItem[],
): GetItineraryImportListItem | undefined => {
	const sorted = items.slice().sort((a, b) => b.lastUpdatedAt - a.lastUpdatedAt)
	const active = sorted.find(
		item => item.status === 'Pending' || item.status === 'Processing',
	)
	if (active) return active
	const nowSec = Date.now() / 1000
	return sorted.find(
		item => (nowSec - item.lastUpdatedAt) * 1000 < RECENT_TERMINAL_MS,
	)
}

const variantForStatus = (status: GetItineraryImportListItem['status']): BannerVariant => {
	if (status === 'Pending' || status === 'Processing') return 'progress'
	if (status === 'Success') return 'success'
	return 'warn'
}

const messageForItem = (item: GetItineraryImportListItem): string => {
	switch (item.status) {
		case 'Pending':
		case 'Processing':
			return "Adding to your itinerary — we'll let you know when it's done"
		case 'Success':
			return `Imported ${item.createdEventCount} ${item.createdEventCount === 1 ? 'event' : 'events'} — review the result`
		case 'NoEventsFound':
			return "Last import didn't find any events"
		case 'Failed':
			return 'Last import failed — open to retry'
	}
}

const ctaForVariant = (variant: BannerVariant): string => {
	if (variant === 'progress') return 'View progress'
	if (variant === 'success') return 'Review'
	return 'Open'
}

const spin = keyframes`
	from { transform: rotate(0deg); }
	to   { transform: rotate(360deg); }
`

const variantBg: Record<BannerVariant, string> = {
	progress: 'rgba(57, 64, 128, 0.08)',
	success: 'rgba(64, 168, 99, 0.12)',
	warn: 'rgba(243, 111, 111, 0.10)',
}

const variantBorder: Record<BannerVariant, string> = {
	progress: 'rgba(57, 64, 128, 0.22)',
	success: 'rgba(64, 168, 99, 0.32)',
	warn: 'rgba(243, 111, 111, 0.30)',
}

const variantIconBg: Record<BannerVariant, string> = {
	progress: blue,
	success: success,
	warn: red,
}

const variantCTA: Record<BannerVariant, string> = {
	progress: blue,
	success: darkOrange,
	warn: darkOrange,
}

const BannerRoot = styled.div<{ $variant: BannerVariant }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 14px;
	margin: 8px 16px 0;
	border-radius: 12px;
	background: ${({ $variant }) => variantBg[$variant]};
	border: 1px solid ${({ $variant }) => variantBorder[$variant]};
	cursor: pointer;
	transition: background 0.15s ease;

	&:hover {
		background: ${({ $variant }) =>
			$variant === 'progress'
				? 'rgba(57, 64, 128, 0.12)'
				: $variant === 'success'
					? 'rgba(64, 168, 99, 0.16)'
					: 'rgba(243, 111, 111, 0.14)'};
	}

	&:focus-visible {
		outline: 2px solid ${orange};
		outline-offset: 2px;
	}

	@media (max-width: 600px) {
		margin: 8px 10px 0;
		padding: 8px 12px;
		gap: 10px;
	}
`

const IconBox = styled.div<{ $variant: BannerVariant }>`
	width: 28px;
	height: 28px;
	border-radius: 8px;
	background: ${({ $variant }) => variantIconBg[$variant]};
	color: ${white};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`

const Glyph = styled.span`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 900;
	font-size: 16px;
	line-height: 1;
`

const Spinner = styled.div`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	border: 2px solid rgba(255, 255, 255, 0.35);
	border-top-color: ${white};
	animation: ${spin} 0.8s linear infinite;
`

const Message = styled.div`
	flex: 1;
	min-width: 0;
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 600;
	font-size: 14px;
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	@media (max-width: 600px) {
		white-space: normal;
	}
`

const CTA = styled.div`
	font-family: 'Lato', system-ui, sans-serif;
	font-weight: 700;
	font-size: 13px;
	color: ${variantCTA.progress};
	flex-shrink: 0;

	@media (max-width: 600px) {
		display: none;
	}
`

const DismissButton = styled.button`
	background: transparent;
	border: none;
	color: ${textMuted};
	width: 24px;
	height: 24px;
	border-radius: 6px;
	cursor: pointer;
	font-size: 18px;
	line-height: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	&:hover {
		background: rgba(0, 0, 0, 0.06);
		color: ${black};
	}
`
